import dynamic from 'next/dynamic';
import type { ComponentType } from 'react';

/**
 * Loads a chart component from this `components/charts/` directory by file name,
 * so a CMS text field can just name a chart:
 *   getChartByName('family-office') -> components/charts/family-office.tsx
 *
 * The name is sanitized (lowercase, `[a-z0-9-]` only) so it can't escape this
 * directory. Returns null for an empty/invalid name; renders nothing if the
 * named file doesn't exist. Drop a new `<name>.tsx` in here and it's loadable —
 * no registration step.
 *
 * Components are memoized per name so callers get a stable identity across
 * renders (a fresh `dynamic()` each time would remount the chart and reset
 * its state).
 *
 * To also expose a chart in the hidden gallery (/[lang]/charts/) and as an
 * iframe embed (/embed/[name]/), add an entry to ./registry.ts.
 */
const cache = new Map<string, ComponentType>();

export function getChartByName(name?: string | null): ComponentType | null {
  const safe = (name ?? '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
  if (!safe) return null;
  let Chart = cache.get(safe);
  if (!Chart) {
    Chart = dynamic(() => import(`./${safe}`).catch(() => ({ default: () => null })));
    cache.set(safe, Chart);
  }
  return Chart;
}
